import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, MapPin, MessagesSquare, Newspaper, Pin, Sparkles } from "lucide-react";
import { apiFetch, API } from "@/lib/api";
import { SponsorStrip } from "@/components/SponsorStrip";
import { useAuth } from "@/context/AuthContext";

const HERO =
  "https://images.unsplash.com/photo-1590866249433-0310439fdab9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1900";

const when = (iso) =>
  new Date(iso).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export default function Landing() {
  const { user, login } = useAuth();
  const [data, setData] = useState(null);
  const [news, setNews] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    apiFetch("/public/photos").then((r) => setPhotos(r.photos)).catch(() => {});
  }, []);

  useEffect(() => {
    apiFetch("/public/sponsors").then((s) => setSponsors(s.sponsors)).catch(() => {});
  }, []);

  useEffect(() => {
    apiFetch("/public/featured").then(setData).catch(() => {});
    apiFetch("/public/news").then((n) => setNews(n.headlines)).catch(() => {});
  }, []);

  const story = data?.story;

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 border-b border-neutral-200 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#B8FF2E] text-[#14210A]">
              <CalendarDays className="h-[18px] w-[18px]" />
            </span>
            <span className="text-[17px] font-extrabold tracking-tight text-neutral-900">Portland Tech</span>
          </Link>
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Link
                  to="/calendar"
                  data-testid="landing-calendar-link"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-100"
                >
                  Calendar
                </Link>
                <Link
                  to="/forum"
                  data-testid="landing-forum-link"
                  className="rounded-full bg-neutral-900 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-700"
                >
                  Member forum
                </Link>
              </>
            ) : (
              <button
                onClick={login}
                data-testid="landing-login-btn"
                className="rounded-full bg-neutral-900 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-700"
              >
                Sign in
              </button>
            )}
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl">
          <img src={HERO} alt="Portland at dusk" className="h-[380px] w-full object-cover sm:h-[480px]" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-transparent" />
          <div className="absolute inset-0 flex flex-col justify-end p-7 sm:p-14">
            <span className="mb-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-neutral-800">
              <Sparkles className="h-3.5 w-3.5 text-[#4D7C0F]" /> Private · invited community leaders
            </span>
            <h1 className="max-w-3xl text-4xl font-extrabold leading-[1.03] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {story?.headline || "Every Tech Gathering in Portland, in one calendar"}
            </h1>
            <p className="mt-4 max-w-xl text-base text-white/85 md:text-lg" data-testid="hero-tagline">
              {story?.tagline ||
                "A private, leader-curated calendar for Portland's tech community — members get every date, RSVPs and the forum."}
            </p>
            <p className="mt-2 max-w-xl text-sm text-white/70">
              {data ? `${data.upcoming_total} upcoming gatherings across Portland and Beaverton.` : "Loading the calendar…"}
            </p>
            {!user && (
              <button
                onClick={login}
                data-testid="hero-login-btn"
                className="mt-7 w-fit rounded-full bg-[#B8FF2E] px-6 py-3 text-sm font-bold text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B]"
              >
                Sign in to see everything
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">This week's five</h2>
            <p className="mt-1 text-sm text-neutral-500 md:text-lg">
              A taste of the calendar. Members see all {data?.upcoming_total ?? "…"} dates.
            </p>
          </div>
          <Link
            to="/calendar"
            data-testid="see-full-calendar"
            className="flex items-center gap-1.5 rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50"
          >
            See the full calendar <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-5" data-testid="featured-grid">
          {(data?.featured || []).map((e, i) => (
            <Link
              key={e.event_id}
              to={user ? `/events/${e.event_id}` : "/calendar"}
              data-testid={`featured-${e.event_id}`}
              className="group block fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="relative overflow-hidden rounded-2xl bg-neutral-100 aspect-video">
                <img
                  src={e.image_url}
                  alt={e.title}
                  className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                />
                {e.pinned && (
                  <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-[#B8FF2E] px-2.5 py-1 text-[11px] font-bold text-[#14210A]">
                    <Pin className="h-3 w-3" /> Featured
                  </span>
                )}
              </div>
              <h3 className="mt-3 text-[15px] font-semibold leading-snug text-neutral-900">{e.title}</h3>
              <p className="mt-1 text-sm text-neutral-500">{when(e.start_at)}</p>
              <p className="mt-0.5 flex items-center gap-1 text-sm text-neutral-500">
                <MapPin className="h-3.5 w-3.5" /> {e.neighborhood}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {photos.length > 0 && (
        <section className="border-t border-neutral-200 bg-neutral-50" data-testid="photo-highlights">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
            <h2 className="text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">From the last few nights</h2>
            <p className="mt-1 text-sm text-neutral-500 md:text-lg">
              Shot by members at real Portland tech events.
            </p>
            <div className={`mt-8 grid gap-3 ${
              photos.length < 4 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"
            }`}>
              {photos.map((p, i) => (
                <div
                  key={p.photo_id}
                  data-testid={`highlight-${p.photo_id}`}
                  className={`group relative overflow-hidden rounded-xl bg-neutral-200 fade-up ${
                    photos.length < 4 ? "aspect-[4/3]" : "aspect-square"
                  }`}
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <img
                    src={`${API}/public/photos/${p.photo_id}/image`}
                    alt={p.caption || p.event_title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/75 to-transparent px-2 py-2 text-[11px] font-semibold text-white">
                    {p.event_title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <SponsorStrip sponsors={sponsors} />

      <section className="border-y border-neutral-200 bg-neutral-50">        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
                Why Portland builds like this
              </h2>
              <p className="mt-4 max-w-2xl leading-relaxed text-neutral-700">{story?.intro}</p>
              <div className="mt-8 grid grid-cols-2 gap-6 sm:grid-cols-4" data-testid="city-stats">
                {(story?.stats || []).map((s) => (
                  <div key={s.label}>
                    <p className="text-3xl font-extrabold tracking-tight text-neutral-900">{s.value}</p>
                    <p className="mt-1 text-xs text-neutral-500">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold tracking-tight text-neutral-900">Where the work is</h3>
              <ul className="mt-4 space-y-4" data-testid="city-sectors">
                {(story?.sectors || []).map((s) => (
                  <li key={s.name} className="border-l-2 border-[#84CC16] pl-4">
                    <p className="text-sm font-bold text-neutral-900">{s.name}</p>
                    <p className="mt-0.5 text-sm text-neutral-600">{s.detail}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[1fr_380px]">
          <div>
            <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
              <Newspaper className="h-5 w-5 text-[#4D7C0F]" /> Portland tech, right now
            </h2>
            <p className="mt-1 text-sm text-neutral-500 md:text-lg">Live headlines from local and national coverage.</p>
            <ul className="mt-6 divide-y divide-neutral-200" data-testid="news-list">
              {news.length === 0 && <li className="py-4 text-sm text-neutral-500">Fetching headlines…</li>}
              {news.map((n) => (
                <li key={n.url} className="py-3.5">
                  <a
                    href={n.url}
                    target="_blank"
                    rel="noreferrer"
                    className="group flex items-start justify-between gap-4"
                  >
                    <span className="text-[15px] font-semibold text-neutral-900 transition-colors duration-200 group-hover:text-[#4D7C0F]">
                      {n.title}
                    </span>
                    <span className="shrink-0 text-xs text-neutral-400">{n.source}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <aside className="rounded-2xl border border-neutral-200 p-7">
            <h3 className="flex items-center gap-2 text-xl font-bold tracking-tight text-neutral-900">
              <MessagesSquare className="h-5 w-5 text-[#4D7C0F]" /> Members' forum
            </h3>
            <p className="mt-2 text-sm text-neutral-600">
              Channels for the whole scene, plus direct messages between members.
            </p>
            <ul className="mt-5 space-y-2" data-testid="channel-teasers">
              {(data?.channels || []).map((c) => (
                <li key={c.slug} className="rounded-xl bg-neutral-50 px-4 py-3">
                  <p className="text-sm font-bold text-neutral-900">{c.name}</p>
                  <p className="text-xs text-neutral-500">{c.blurb}</p>
                </li>
              ))}
            </ul>
            {user ? (
              <Link
                to="/forum"
                data-testid="forum-cta"
                className="mt-6 block rounded-full bg-[#B8FF2E] px-5 py-3 text-center text-sm font-bold text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B]"
              >
                Open the forum
              </Link>
            ) : (
              <button
                onClick={login}
                data-testid="forum-login-cta"
                className="mt-6 w-full rounded-full bg-[#B8FF2E] px-5 py-3 text-sm font-bold text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B]"
              >
                Sign in to join
              </button>
            )}
            <p className="mt-4 text-xs text-neutral-500">{story?.note}</p>
          </aside>
        </div>
      </section>

      <footer className="border-t border-neutral-200 py-10">
        <div className="mx-auto max-w-7xl px-4 text-sm text-neutral-500 sm:px-6 lg:px-8">
          Portland Tech Calendar · listings curated by invited community leaders.
        </div>
      </footer>
    </div>
  );
}
